/* eslint indent: 0 */
import axios, { AxiosRequestConfig } from 'axios'
import { openModal } from 'sb-modal/manager'
import links from 'common/helpers/links'
import config from 'app-config'


type Config<Payload, ModifiedResult> = AxiosRequestConfig & {
  // you can add additional params below
  once?: boolean // only one request at time can be active
  cancelPrevious?: boolean // cancel previous request with same name and start new one
  withErrorModal?: boolean
  modifyResult?: (data: ModifiedResponseData<Payload>) => ModifiedResult
}

type Body<Variables> = {
  query: string,
  variables?: Variables
}

type AxiosResponse<Payload> = {
  data: Payload
  errors: { [key: string]: any }
}

type ModifiedResponseData<Payload extends { [key: string]: any }> = {
  [K in keyof Payload]: Omit<Payload[K], 'error'> & {
    error: {
      code: string
      message: string
    }
  }
}


export class DummyPromise {
  then() { return this } // eslint-disable-line
  catch() {}
}

// only one request can be sent, if action will be called until this request is active then this request to be returned
const onceRequests = new Map()

// if action will be called until request is active then this request to be canceled and new started
const cancelableRequests = new Map()

const request = axios.create({
  baseURL: config.services.graphql,
  withCredentials: true,
  timeout: 30 * 1000,
})

export const modifyResponseData = <Payload>(data): ModifiedResponseData<Payload> => {
  let result = {} as ModifiedResponseData<Payload>

  Object.keys(data).forEach((key) => {
    const { data: keyData, error: keyError, ...otherValues } = data[key]
    let error = null

    if (keyError) {
      /*

        in .graphql schemes multiple error keys possible:

        error {
          ... on SecurityError {
            securityErrorCode: errorCode
            message
          }
          ... on ServerError {
            serverErrorCode: errorCode
            message
          }
        }

       */
      const { message, ...otherKeys } = keyError // { securityErrorCode: 'NOT_AUTHENTICATED', message: 'Auth required' }
      const [ errorCodeKey ] = Object.keys(otherKeys) // [ 'securityErrorCode' ]
      const code = keyError[errorCodeKey] // 401

      // ATTN should add "hastRedirect" to result to cancel request and redirect to /register page
      if (code === 'NOT_AUTHENTICATED') {
        // @ts-ignore
        result.hasRedirect = true
      }

      error = { code, message }
    }

    result[key] = {
      ...otherValues, // for example in purchaseCommit there are { session, data, error }
      data: keyData,
      error,
    }
  })

  return result
}

const createRequest = <Variables, Payload, ModifiedResult>(
  name: string,
  body: Body<Variables>,
  config: Config<Payload, ModifiedResult>
) => {
  const { modifyResult, withErrorModal = true, ...restConfig } = config

  return request.post<AxiosResponse<Payload>>(`graphql?name=RECURLY_${name}`, body, restConfig)
    .then(({ data: responseData }) => {
      onceRequests.delete(name)
      cancelableRequests.delete(name)

      if (!responseData) {
        return Promise.reject(new Error('response.data does not exist!'))
      }

      const { data, errors } = responseData

      // ATTN here we handle backend "errors"
      if (errors) {
        return Promise.reject({ errors }) // we should fail request if "errors" exist (discussed with @donbeave)
      }

      return data
    })
    // @ts-ignore
    .then((data) => {
      const modifiedResponseData = modifyResponseData<Payload>(data)

      // ATTN redirect to /register on "NOT_AUTHENTICATED" errorCode
      // @ts-ignore
      if (modifiedResponseData.hasRedirect) {
        const redirectLink = encodeURIComponent(`${window.location.pathname}${window.location.search}`)
        window.location.href = `${links.login}?redirect=${redirectLink}`

        return new DummyPromise()
      }

      if (typeof modifyResult === 'function') {
        return modifyResult(modifiedResponseData)
      }

      return modifiedResponseData
    })
    .catch((error) => {
      onceRequests.delete(name)
      cancelableRequests.delete(name)

      if (axios.isCancel(error)) {
        console.log('Operation cancelled')

        return Promise.reject({ isCanceled: true })
      }

      console.error(error)

      // https://scentbird.atlassian.net/browse/CB-185
      // user readable error should be always exists on axios error
      // set "Network: Offline Throttling" to check "Network error"
      if (error.isAxiosError) {
        try {
          const { code, message } = error.toJSON()
          const errorMessage = [ code, message ].filter((v) => v).join(': ')

          openModal('somethingWentWrong', {
            hideIcon: true,
            error: errorMessage,
          })
        }
        catch (err) {
          console.error(err)
        }
      }
      else if (withErrorModal) {
        openModal('somethingWentWrong', {
          hideIcon: true,
          error: error.errors || error,
        })
      }

      return Promise.reject(error)
    })
}

export default <Variables, Payload, ModifiedResult = ModifiedResponseData<Payload>>(
  name: string,
  body: Body<Variables>,
  config?: Config<Payload, ModifiedResult>
): Promise<ModifiedResult> => {
  const { once, cancelPrevious, ...restConfig } = config || {}

  if (once) {
    const previousRequest = onceRequests.get(name)

    if (previousRequest) {
      return previousRequest
    }
  }
  else if (cancelPrevious) {
    const previousRequest = cancelableRequests.get(name)

    if (previousRequest) {
      previousRequest.cancel()
    }

    const CancelToken = axios.CancelToken
    const source = CancelToken.source()

    restConfig.cancelToken = source.token
    cancelableRequests.set(name, source)
  }

  const request = createRequest<Variables, Payload, ModifiedResult>(name, body, restConfig)

  if (once) {
    onceRequests.set(name, request)
  }

  // @ts-ignore
  return request
}
